import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import { snackbar, getStateSingleAutocompleteInitialValues, Page } from '@occc-app/design-system';
import {
  HoursOfOperationUpdate,
  useSiteManagementLocationUpdateMutation,
  RetailerNetworkLocationDataFragment,
} from '@occc/graphql';
import { parseDate, DateTimeFormatParse } from '@occc-app/lib/date';

import { RetailerNetworkLocationUpdatePage } from './retailer-network-location-update-page.component';
import {
  RetailerNetworkLocationUpdateFormValues,
  LOCATION_ATTRIBUTE_VALUE,
} from './retailer-network-location-update-page.types';
import { prepareFormInputs, clearTime } from './retailer-network-location-update-page.utils';
import { PAGE_TITLE } from './retailer-network-location-update-page.constants';

import { useLocationBulkOperationHoursUpdateForRetailerNetwork } from '../../data-access/mutations';
import { useGetRetailerNetworkLocations } from '../../data-access/queries';
import { routeCreator } from '../retailer-connect.utils';
import { RetailerNetworkErrorMessage } from '../components/retailer-network-error-message.component';
import { RetailerNetworkNoData } from '../components/retailer-network-no-data.component';
import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';

export const RetailerNetworkLocationUpdatePageContainer = () => {
  const navigate = useNavigate();
  const { networkId = '', locationId = '' } = useParams<{
    networkId: string;
    locationId: string;
  }>();

  const { loading, data, error } = useGetRetailerNetworkLocations({
    filter: {
      retailerNetworkUuidEquals: networkId,
      retailerNetworkLocationUuidEquals: locationId,
    },
  });

  const retailerNetworkLocation: RetailerNetworkLocationDataFragment | undefined =
    data?.[0].location;
  const locationName =
    retailerNetworkLocation?.name ||
    retailerNetworkLocation?.siteSystemConfiguration?.[0]?.identifier;

  const breadcrumbLinks = useMemo(
    () => [
      { label: 'My Stores', to: routeCreator.myStores(networkId) },
      {
        label: locationName || 'Store Details',
        to: routeCreator.locationDetails(networkId, locationId),
      },
      { label: PAGE_TITLE },
    ],
    [locationName, networkId, locationId],
  );

  useBreadcrumbLinks(breadcrumbLinks);

  const [locationUpdate] = useSiteManagementLocationUpdateMutation({
    onCompleted: (data) => {
      const uuid = data?.locationUpdate?.uuid;
      if (uuid) {
        navigate(routeCreator.locationDetails(networkId, locationId), { replace: true });

        snackbar.success('Location was successfully updated');
      }
    },
  });

  const [bulkOperationHoursUpdateForRetailerNetwork] =
    useLocationBulkOperationHoursUpdateForRetailerNetwork({});

  const retailerNetworkLocationUpdate = async (values: RetailerNetworkLocationUpdateFormValues) => {
    const updateValues = prepareFormInputs(locationId, values);

    if (!updateValues) {
      return;
    }

    locationUpdate({
      ...updateValues,
      refetchQueries: ['retailerNetworkLocations'],
    });

    if (values.applyToAllStores) {
      const schedule: HoursOfOperationUpdate[] =
        updateValues.variables.locationUpdateInput.hoursOfOperationGroup.hours;

      bulkOperationHoursUpdateForRetailerNetwork({
        variables: {
          retailerNetworkUuid: networkId,
          schedule,
        },
      });
    }
  };

  const goBack = () => {
    // TODO: change back to navigate(-1); when OCST-10385 done
    navigate(routeCreator.locationDetails(networkId, locationId), { replace: true });
  };

  const initialValues = useMemo(() => {
    const attributes =
      retailerNetworkLocation?.attributes && retailerNetworkLocation.attributes.length
        ? retailerNetworkLocation.attributes
            .filter((attribute) => attribute.value === LOCATION_ATTRIBUTE_VALUE.YES)
            .map((attribute) => attribute.name)
        : [];

    const details = {
      name: retailerNetworkLocation?.name || '',
      street1: retailerNetworkLocation?.address?.street1 || '',
      street2: retailerNetworkLocation?.address?.street2 || '',
      city: retailerNetworkLocation?.address?.city || '',
      state: retailerNetworkLocation?.address
        ? getStateSingleAutocompleteInitialValues(retailerNetworkLocation.address.state)
        : undefined,
      zipCode: retailerNetworkLocation?.address?.zipCode || '',
      phoneNumber: retailerNetworkLocation?.phoneNumbers[0]?.phoneNumber || undefined,
      availableServices: attributes,
      brandingDetails: retailerNetworkLocation?.brandingDetails
        ? {
            primaryColor: retailerNetworkLocation.brandingDetails.primaryColor || null,
            secondaryColor: retailerNetworkLocation.brandingDetails.secondaryColor || null,
          }
        : null,
    };

    const geoLocation = retailerNetworkLocation?.address?.geoLocation
      ? {
          latitude: retailerNetworkLocation.address?.geoLocation.latitude,
          longitude: retailerNetworkLocation.address?.geoLocation.longitude,
        }
      : null;

    const locationHoursOfOperation = retailerNetworkLocation?.hoursOfOperationGroup?.hours || [];
    const schedule = locationHoursOfOperation.reduce((acc, operationDay) => {
      return {
        ...acc,
        [operationDay.dayOfWeek]: {
          start: parseDate(clearTime(operationDay.openTime), DateTimeFormatParse.Time24h),
          end: parseDate(clearTime(operationDay.closeTime), DateTimeFormatParse.Time24h),
          isOpenAllDay: operationDay.openTime && operationDay.openTime === operationDay.closeTime,
        },
      };
    }, {});
    const availableDays = locationHoursOfOperation.map((day) => day.dayOfWeek);

    return {
      availableDays,
      geoLocation,
      schedule,
      details,
      applyToAllStores: false,
    };
  }, [
    retailerNetworkLocation?.address,
    retailerNetworkLocation?.attributes,
    retailerNetworkLocation?.brandingDetails,
    retailerNetworkLocation?.hoursOfOperationGroup?.hours,
    retailerNetworkLocation?.name,
    retailerNetworkLocation?.phoneNumbers,
  ]);

  if (error) {
    return <RetailerNetworkErrorMessage />;
  }

  if (loading && !retailerNetworkLocation) {
    return (
      <Box p={3} display='flex' justifyContent='center' alignItems='center'>
        <CircularProgress />
      </Box>
    );
  }

  if (!retailerNetworkLocation) {
    return <RetailerNetworkNoData />;
  }

  return (
    <RetailerNetworkLocationUpdatePage
      location={retailerNetworkLocation}
      initialValues={initialValues}
      goBack={goBack}
      onSubmit={retailerNetworkLocationUpdate}
    />
  );
};
